import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  forwardRef,
} from "react";
import { Modal } from "react-bootstrap";
import SnackBarTool from "../../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
import { useFormik } from "formik";
import {
  createLock,
  getAvailableLockList,
  mapLockToSite,
  removeLockSiteMapping,
} from "../../../_redux/franchises/franchisesCrud";
import { 
  MenuItem, 
  Select, 
  CircularProgress
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FixedSizeList as List } from "react-window";
import PropTypes from "prop-types";

// CSS styles for virtualized options
const virtualizedStyles = `
  .virtualized-option {
    transition: background-color 0.2s ease;
  }
  .virtualized-option:hover {
    background-color: #f5f5f5 !important;
  }
  .virtualized-option.selected {
    background-color: #e3f2fd !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('virtualized-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = 'virtualized-styles';
  styleSheet.innerText = virtualizedStyles;
  document.head.appendChild(styleSheet);
}

const STATUS_LIST = [
  { id: "1", name: "Active" },
  { id: "0", name: "Inactive" },
];

/**
 * Resolves the lock mapped to a site gate from the franchise site list payload.
 * When `locks` is an array (including empty), only that array defines mappings — flat `lock_*` fields
 * are ignored so stale top-level IDs from other gates/sites do not appear in the modal.
 * If `locks` is missing (not an array), `gate_1` may still be resolved from legacy flat fields.
 *
 * @param {object|null|undefined} siteRow - Single site object from the API.
 * @param {"gate_1"|"gate_2"} gate - Gate slot (`gate_1` or `gate_2`).
 * @returns {{ id: string|number, display_name: string, serial_number: string }|null} Mapped lock or null.
 */
function getMappedLockForGate(siteRow, gate) {
  if (!siteRow || typeof siteRow !== "object") return null;
  const locks = siteRow.locks;
  if (Array.isArray(locks)) {
    const match = locks.find(
      (L) =>
        L &&
        L.gate_number != null &&
        String(L.gate_number).toLowerCase() === String(gate).toLowerCase()
    );
    if (match != null && match.id != null) {
      return {
        id: match.id,
        display_name:
          typeof match.display_name === "string" ? match.display_name : "",
        serial_number:
          typeof match.serial_number === "string" ? match.serial_number : "",
      };
    }
    return null;
  }
  if (gate === "gate_1") {
    const id = siteRow.lock_id;
    if (id == null) return null;
    const flatGate = siteRow.lock_gate_number;
    if (
      flatGate != null &&
      String(flatGate).toLowerCase() !== "gate_1"
    ) {
      return null;
    }
    return {
      id,
      display_name:
        siteRow.lock_display_name != null
          ? String(siteRow.lock_display_name)
          : "",
      serial_number:
        siteRow.lock_serial_number != null
          ? String(siteRow.lock_serial_number)
          : "",
    };
  }
  return null;
}

/**
 * Builds display fields for a lock chosen in the gate-mapping picker.
 *
 * @param {string|number} lockId - Selected lock id.
 * @param {Array<{ id: string|number, display_name?: string, serial_number?: string }>} availableLocks - Franchise available locks.
 * @param {Array<{ value: string|number, label?: string, serial?: string }>} lockOptions - Select options derived from available locks.
 * @returns {{ id: string|number, display_name: string, serial_number: string }} Lock row for read-only display.
 */
function resolveLockFromSelection(lockId, availableLocks, lockOptions) {
  const fromList = (availableLocks || []).find(
    (l) => String(l.id) === String(lockId)
  );
  if (fromList) {
    return {
      id: fromList.id,
      display_name:
        typeof fromList.display_name === "string" ? fromList.display_name : "",
      serial_number:
        typeof fromList.serial_number === "string"
          ? fromList.serial_number
          : "",
    };
  }
  const fromOption = (lockOptions || []).find(
    (o) => String(o.value) === String(lockId)
  );
  if (fromOption) {
    return {
      id: lockId,
      display_name: typeof fromOption.label === "string" ? fromOption.label : "",
      serial_number:
        typeof fromOption.serial === "string" ? fromOption.serial : "",
    };
  }
  return { id: lockId, display_name: "", serial_number: "" };
}

// Virtualized option component for react-window
const VirtualizedOption = forwardRef(({ index, style, data }, ref) => {
  const { options, onSelect, selectedValue } = data;
  const option = options[index];
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onSelect(option.value);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(option.value);
  };

  // Truncate long text with ellipsis
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Check if text is long
  const isLongName = option.label && option.label.length > 50;
  const isLongSerial = option.serial && option.serial.length > 30;
  
  return (
    <div
      ref={ref}
      style={{
        ...style,
        padding: '8px 16px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: selectedValue === option.value ? '#e3f2fd' : 'transparent',
        display: 'flex',
        alignItems: 'flex-start',
        minHeight: isLongName || isLongSerial ? '64px' : '48px',
        boxSizing: 'border-box',
        transition: 'background-color 0.2s ease'
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={(e) => e.preventDefault()}
      className={`virtualized-option ${selectedValue === option.value ? 'selected' : ''}`}
      role="option"
      aria-selected={selectedValue === option.value}
      tabIndex={0}
      aria-label={`${option.label}${option.serial ? `, Serial: ${option.serial}` : ''}`}
      title={`${option.label}${option.serial ? ` (Serial: ${option.serial})` : ''}`}
    >
      <div style={{ 
        flex: 1, 
        minWidth: 0, // Allows flex item to shrink below content size
        overflow: 'hidden'
      }}>
        <div style={{ 
          fontWeight: '500',
          fontSize: '14px',
          lineHeight: '1.4',
          wordBreak: 'break-word',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: isLongName ? 2 : 1,
          WebkitBoxOrient: 'vertical'
        }}>
          {option.label}
        </div>
        {option.serial && (
          <div style={{ 
            fontSize: '0.8rem', 
            color: '#666', 
            marginTop: '2px',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            Serial: {truncateText(option.serial, 30)}
          </div>
        )}
      </div>
      
      {/* Selection indicator */}
      {selectedValue === option.value && (
        <div style={{
          marginLeft: '8px',
          color: '#1976d2',
          fontSize: '16px',
          flexShrink: 0
        }}>
          ✓
        </div>
      )}
    </div>
  );
});

VirtualizedOption.displayName = 'VirtualizedOption';

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('LockModal Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Something went wrong!</h4>
          <p>An error occurred while loading the lock modal. Please try again.</p>
          <button 
            className="btn btn-outline-danger btn-sm"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

// Optimized FormField component
const FormField = React.memo(({
  label,
  formik,
  name,
  disabled,
  type = "text",
  isSelect = false,
}) => {
  if (isSelect) {
    return (
      <div>
        <label htmlFor={name} className="mt-3" style={{ display: 'block', marginBottom: '8px' }}>
          {label}
        </label>
        <Select
          name={name}
          id={name}
          disabled={disabled}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            getContentAnchorEl: null,
            PaperProps: {
              role: 'listbox',
              'aria-label': `${label} options`
            }
          }}
          className="form-control"
          IconComponent={(props) => (
            <i {...props}>
              <KeyboardArrowDownIcon />
            </i>
          )}
          inputProps={{ 
            "aria-label": label,
            "aria-describedby": formik.touched[name] && formik.errors[name] ? `${name}-error` : undefined
          }}
          displayEmpty
          {...formik.getFieldProps(name)}
        >
          {STATUS_LIST.map((status) => (
            <MenuItem key={status.id} value={status.id} role="option">
              {status.name}
            </MenuItem>
          ))}
        </Select>
        {formik.touched[name] && formik.errors[name] && (
          <div className="fv-plugins-message-container">
            <div 
              id={`${name}-error`}
              className="fv-help-block" 
              role="alert"
              aria-live="polite"
            >
              {formik.errors[name]}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <label htmlFor={name} className="mt-3" style={{ display: 'block', marginBottom: '8px' }}>
        {label}
      </label>
      <input
        type={type}
        name={name}
        id={name}
        placeholder={label}
        className="form-control"
        disabled={disabled}
        aria-describedby={formik.touched[name] && formik.errors[name] ? `${name}-error` : undefined}
        aria-invalid={formik.touched[name] && formik.errors[name] ? 'true' : 'false'}
        {...formik.getFieldProps(name)}
      />
      {formik.touched[name] && formik.errors[name] && (
        <div className="fv-plugins-message-container">
          <div 
            id={`${name}-error`}
            className="fv-help-block" 
            role="alert"
            aria-live="polite"
          >
            {formik.errors[name]}
          </div>
        </div>
      )}
      <div className="mt-2" style={{ textAlign: "end" }} />
    </div>
  );
});

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  formik: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  isSelect: PropTypes.bool,
};

// Custom Virtualized Searchable Select Component
const VirtualizedSearchableSelect = React.memo(({
  options = [],
  value,
  onChange,
  placeholder = "Search...",
  disabled = false,
  loading = false,
  height = 300,
  itemHeight = 48,
  searchPlaceholder = "Search options...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return options;
    
    const term = debouncedSearchTerm.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(term) ||
      (option.serial && option.serial.toLowerCase().includes(term))
    );
  }, [options, debouncedSearchTerm]);

  // Prepare data for virtualized list
  const listData = useMemo(() => ({
    options: filteredOptions,
    onSelect: (selectedValue) => {
      onChange(selectedValue);
      setIsOpen(false);
      setSearchTerm("");
    },
    selectedValue: value,
  }), [filteredOptions, onChange, value]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      }, 100);
    }
  }, [disabled, isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClose]);

  return (
    <div ref={containerRef} style={{ minWidth: 320, position: 'relative' }}>
      <label htmlFor="lock-select" className="mt-3" style={{ display: 'block', marginBottom: '8px' }}>
        Available Locks
      </label>
      
      {/* Custom Dropdown Trigger */}
      <div
        ref={dropdownRef}
        onClick={handleToggle}
        style={{
          border: '1px solid #c4c4c4',
          borderRadius: '4px',
          padding: '8px 12px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '40px',
          boxSizing: 'border-box'
        }}
      >
        <span 
          style={{ 
            color: selectedOption ? '#000' : '#999',
            fontSize: '14px',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginRight: '8px'
          }}
          title={selectedOption ? selectedOption.label : "Select a lock"}
        >
          {selectedOption ? selectedOption.label : "Select a lock"}
        </span>
        <KeyboardArrowDownIcon 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0
          }} 
        />
      </div>

      {/* Custom Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #c4c4c4',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxHeight: height,
            overflow: 'hidden',
            minWidth: '320px'
          }}
        >
          {/* Search Input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleClose();
                }
              }}
            />
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                <CircularProgress size={20} />
              </div>
            )}
          </div>
          
          {/* Virtualized Options List */}
          {!loading && filteredOptions.length > 0 && (
            <div style={{ height: Math.min(filteredOptions.length * itemHeight, height - 100) }}>
              <List
                height={Math.min(filteredOptions.length * itemHeight, height - 100)}
                itemCount={filteredOptions.length}
                itemSize={itemHeight}
                itemData={listData}
              >
                {VirtualizedOption}
              </List>
            </div>
          )}
          
          {!loading && filteredOptions.length === 0 && (
            <div style={{ 
              padding: '16px', 
              textAlign: 'center', 
              color: '#999',
              fontSize: '14px'
            }}>
              {debouncedSearchTerm ? "No locks found" : "No locks available"}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

VirtualizedSearchableSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    serial: PropTypes.string,
  })),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  height: PropTypes.number,
  itemHeight: PropTypes.number,
  searchPlaceholder: PropTypes.string,
};

/**
 * Modal for franchise lock CRUD and per-site gate mapping (`gate_1` / `gate_2`).
 *
 * @param {object} props - Component props.
 * @param {boolean|object} props.dialog - Open state or `{ check, data, addLock?, gate_number? }` from the site row.
 * @param {Function} props.setDialog - Closes or updates dialog state.
 * @param {string|number} [props.orderId] - Optional order context.
 * @param {string|number} props.fraId - Franchise id for lock APIs.
 * @param {string} [props.edit] - `"edit"` when editing an existing lock.
 * @param {object} [props.currentQueryParams] - Optional list query params.
 * @param {Function} [props.onLockEditSuccess] - Called after successful map/remove (or lock save); e.g. refetch site list.
 * @returns {JSX.Element} Lock modal UI.
 */
const LockModal = ({
  dialog,
  setDialog,
  orderId,
  fraId,
  edit,
  currentQueryParams,
  onLockEditSuccess,
}) => {
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [availableLocks, setAvailableLocks] = useState([]);
  const [siteLock, setSiteLock] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);

  const isDialogOpen = Boolean(
    dialog && (typeof dialog === "object" ? dialog.check !== false : dialog)
  );

  const handleCloseModal = useCallback(() => {
    setDialog(false);
  }, [setDialog]);

  /** Which franchise site gate slot is being mapped (`gate_1` default when omitted). */
  const gateNumber = useMemo(() => {
    if (!dialog?.addLock) return "gate_1";
    return dialog.gate_number === "gate_2" ? "gate_2" : "gate_1";
  }, [dialog]);

  const initialValues = useMemo(() => {
    const mappedGate =
      dialog?.addLock && dialog?.data
        ? getMappedLockForGate(dialog.data, gateNumber)
        : null;

    return {
      franchise_id: Number(fraId) || "",
      site_id:
        edit === "edit" ? dialog?.data?.site_id : dialog?.data?.id || "",
      display_name: edit === "edit" ? dialog?.data?.display_name : "",
      serial_number: edit === "edit" ? dialog?.data?.serial_number : "",
      status: edit === "edit" ? dialog?.data?.status : "",
      addLock: dialog?.addLock,
      lock_id: dialog?.addLock
        ? mappedGate?.id ?? ""
        : edit === "edit"
        ? dialog?.data?.lock_id
        : dialog?.data?.lock_id ?? "",
    };
  }, [fraId, edit, dialog, gateNumber]);

  // Transform available locks for the virtualized select
  const lockOptions = useMemo(() => 
    availableLocks.map(lock => ({
      value: lock.id,
      label: lock.display_name,
      serial: lock.serial_number,
    })), 
    [availableLocks]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (edit !== "edit") {
        delete values.status;
      }

      setLoading(true);
      setSnack({ call: false, type: "", message: "" });

      createLock(values, edit, dialog?.data?.id)
        .then((res) => {
          setLoading(false);
          formik.resetForm();

          setSnack({
            call: true,
            type: "success",
            message:
              res?.data?.message ?? "Operation completed successfully",
          });

          if (onLockEditSuccess) {
            onLockEditSuccess();
          }

          setTimeout(() => {
            handleCloseModal();
          }, 1000);
        })
        .catch((error) => {
          setLoading(false);
          const errorMessage =
            error?.response?.data?.message ??
            error?.message ??
            "An unexpected error occurred";
          setSnack({
            call: true,
            type: "error",
            message: errorMessage,
          });
          console.error('Lock creation/update error:', error);
        });
    },
  });

  const siteId = formik.values.site_id;

  /** Reloads the available-lock picker; failures are logged and do not reject callers. */
  const reloadAvailableLocks = useCallback(() => {
    if (!siteId || !fraId) {
      return Promise.resolve();
    }
    return getAvailableLockList({
      franchise_id: fraId,
      site_id: siteId,
    })
      .then((availableRes) => {
        setAvailableLocks(availableRes.data?.data || []);
      })
      .catch((error) => {
        console.error("Error reloading available locks:", error);
      });
  }, [fraId, siteId]);

  // When addLock flag is true, load available locks and set existing from dialog
  useEffect(() => {
    if (!dialog?.addLock || !siteId) {
      return undefined;
    }
    let active = true;
    setMappingLoading(true);
    setSiteLock(null);
    getAvailableLockList({
      franchise_id: fraId,
      site_id: siteId,
    })
      .then((availableRes) => {
        if (!active) return;
        setAvailableLocks(availableRes.data?.data || []);
        const mapped = getMappedLockForGate(dialog?.data, gateNumber);
        if (mapped) {
          setSiteLock({
            id: mapped.id,
            display_name: mapped.display_name,
            serial_number: mapped.serial_number,
          });
        } else {
          setSiteLock(null);
        }
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error loading available locks:", error);
        setSnack({
          call: true,
          type: "error",
          message: "Failed to load available locks. Please try again.",
        });
      })
      .finally(() => {
        if (active) setMappingLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dialog, fraId, siteId, gateNumber]);

  const handleMapToSite = useCallback(() => {
    const lockId = formik.values.lock_id;
    if (!lockId) return;
    setMappingLoading(true);
    mapLockToSite({
      lock_id: lockId,
      site_id: siteId,
      gate_number: gateNumber,
    })
      .then((res) => {
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message ?? "Lock mapped successfully",
        });
        setSiteLock(
          resolveLockFromSelection(lockId, availableLocks, lockOptions)
        );
        formik.setFieldValue("lock_id", "");
        if (onLockEditSuccess) {
          onLockEditSuccess();
        }
        return reloadAvailableLocks();
      })
      .catch((error) => {
        console.error("Error mapping lock to site:", error);
        setSnack({
          call: true,
          type: "error",
          message:
            error?.response?.data?.message ?? "Failed to map lock to site",
        });
      })
      .finally(() => setMappingLoading(false));
  }, [
    siteId,
    availableLocks,
    lockOptions,
    formik,
    gateNumber,
    onLockEditSuccess,
    reloadAvailableLocks,
  ]);

  const handleRemoveSiteMapping = useCallback(() => {
    if (!siteLock) return;
    setMappingLoading(true);
    removeLockSiteMapping({
      lock_id: siteLock.id,
      site_id: siteId,
      gate_number: gateNumber,
    })
      .then((res) => {
        setSnack({
          call: true,
          type: "success",
          message:
            res?.data?.message ?? "Lock mapping removed successfully",
        });
        setSiteLock(null);
        formik.setFieldValue("lock_id", "");
        if (onLockEditSuccess) {
          onLockEditSuccess();
        }
        return reloadAvailableLocks();
      })
      .catch((error) => {
        console.error("Error removing lock mapping:", error);
        setSnack({
          call: true,
          type: "error",
          message:
            error?.response?.data?.message ??
            "Failed to remove lock mapping",
        });
      })
      .finally(() => setMappingLoading(false));
  }, [
    siteLock,
    siteId,
    gateNumber,
    formik,
    onLockEditSuccess,
    reloadAvailableLocks,
  ]);

  return (
    <ErrorBoundary>
      <Modal
        show={isDialogOpen}
        onHide={handleCloseModal}
        aria-labelledby="example-modal-sizes-title-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title id="example-modal-sizes-title-lg">
            {edit === "edit"
              ? "Edit Lock"
              : dialog?.addLock
              ? gateNumber === "gate_2"
                ? "Map Lock to Gate 2"
                : "Map Lock to Gate 1"
              : "Add a New Lock"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {dialog?.addLock ? (
          <div>
            {siteLock ? (
              <div className="d-flex flex-column">
                <div
                  className="d-flex"
                  style={{ gap: "20px", alignItems: "center" }}
                >
                  <div>
                    <div>Lock Name</div>
                    <input
                      className="form-control bg-light"
                      readOnly
                      value={siteLock.display_name}
                    />
                  </div>
                  <div>
                    <div>Serial Number</div>
                    <input
                      className="form-control bg-light"
                      readOnly
                      value={siteLock.serial_number}
                    />
                  </div>
                  <button
                    className="btn btn-primary btn-md"
                    style={{ marginTop: "35px" }}
                    onClick={handleRemoveSiteMapping}
                    disabled={mappingLoading}
                  >
                    {mappingLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" />{" "}
                        Loading...
                      </>
                    ) : (
                      "Remove"
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="d-flex"
                style={{ gap: "20px", alignItems: "center" }}
              >
                <VirtualizedSearchableSelect
                  options={lockOptions}
                  value={formik.values.lock_id}
                  onChange={(value) => formik.setFieldValue("lock_id", value)}
                  disabled={mappingLoading}
                  loading={mappingLoading}
                  searchPlaceholder="Search locks by name or serial..."
                  height={300}
                  itemHeight={48}
                />
                <button
                  className="btn btn-primary btn-md"
                  style={{ marginTop: "35px" }}
                  onClick={handleMapToSite}
                  disabled={!formik.values.lock_id || mappingLoading}
                >
                  {mappingLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />{" "}
                      Loading...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <FormField
              label="Lock Name"
              formik={formik}
              name="display_name"
              disabled={loading}
            />
            <FormField
              label="Serial Number"
              formik={formik}
              name="serial_number"
              disabled={edit === "edit" ? true : loading}
            />
            {edit === "edit" && (
              <FormField
                label="Status"
                formik={formik}
                name="status"
                disabled={loading}
                isSelect
              />
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div>
          <button
            type="button"
            onClick={handleCloseModal}
            className="btn btn-light btn-elevate"
            disabled={loading || mappingLoading}
          >
            Close
          </button>

          {!dialog?.addLock && (
            <button
              type="button"
              className={`btn ${
                loading ? "btn-secondary" : "btn-delete"
              } btn-elevate`}
              onClick={() => formik.submitForm()}
              disabled={
                loading ||
                formik.values.display_name === "" ||
                formik.values.serial_number === ""
              }
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  />
                  {edit === "edit" ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>{edit === "edit" ? "UPDATE" : "ADD"}</>
              )}
            </button>
          )}
        </div>
        </Modal.Footer>
        {snack.call && <SnackBarTool {...snack} />}
      </Modal>
    </ErrorBoundary>
  );
};

LockModal.propTypes = {
  dialog: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      check: PropTypes.bool,
      data: PropTypes.object,
      addLock: PropTypes.bool,
      gate_number: PropTypes.oneOf(["gate_1", "gate_2"]),
    }),
  ]).isRequired,
  setDialog: PropTypes.func.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fraId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  edit: PropTypes.string,
  currentQueryParams: PropTypes.object,
  onLockEditSuccess: PropTypes.func,
};

export default LockModal;
